import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { WARRANTY_RECORDS_LIST, WARRANTY_RECORD_DELETE } from '../../graphql/warrantyRecords';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Button, TableSortLabel, Box, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';

const columns = [
  { id: 'warrantyNumber', label: 'Warranty Number' },
  { id: 'provider', label: 'Provider' },
  { id: 'startDate', label: 'Start Date' },
  { id: 'endDate', label: 'End Date' },
  { id: 'status', label: 'Status' },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const WarrantyRecordList = () => {
  const { data, loading, error, refetch } = useQuery(WARRANTY_RECORDS_LIST);
  const [deleteWarrantyRecord] = useMutation(WARRANTY_RECORD_DELETE, { onCompleted: () => refetch() });
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('warrantyNumber');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, warrantyRecord: null });
  const navigate = useNavigate();

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleEdit = (id) => navigate(`/fleet/warranty-records/${id}`);
  const handleAdd = () => navigate('/fleet/warranty-records/new');

  const handleDeleteClick = (warrantyRecord) => setDeleteDialog({ open: true, warrantyRecord });
  const handleDeleteConfirm = async () => {
    await deleteWarrantyRecord({ variables: { id: deleteDialog.warrantyRecord.id } });
    setDeleteDialog({ open: false, warrantyRecord: null });
  };
  const handleDeleteCancel = () => setDeleteDialog({ open: false, warrantyRecord: null });

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading warranty records.</div>;

  const rows = data?.warrantyRecordsList?.items || [];
  const sortedRows = [...rows].sort(getComparator(order, orderBy));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="primary" onClick={handleAdd}>Add Warranty Record</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.warrantyNumber}</TableCell>
                <TableCell>{row.provider}</TableCell>
                <TableCell>{row.startDate}</TableCell>
                <TableCell>{row.endDate}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(row.id)} size="small"><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(row)} size="small" color="error"><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Warranty Record</DialogTitle>
        <DialogContent>Are you sure you want to delete this warranty record?</DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarrantyRecordList; 